// Shared construction of tenant-scoped SWR cache keys.
//
// Tenant-scoped endpoints are addressed by a URL that says nothing about the tenant — that travels
// in the X-Tenant-Id header — so without a suffix every tenant shares one cache entry and a switch
// can serve the previous tenant's data. The URL stays at the front of the key so the existing
// prefix-matching revalidation helpers (and the tenant-change sweep in TenantSelectionContext,
// which matches on `api/`) keep finding these entries.
//
// Render-time callers should use the `useTenantScopedKey` hook, which reads the tenant reactively.
// This module additionally serves imperative call sites — cache writes after a save — which run
// well after the provider has committed the change and so can read it back from storage.

const TENANT_ID_STORAGE_KEY = 'tenantId';

/** Marker that makes a tenant-scoped cache entry recognisable, so it can be swept on a tenant change. */
const TENANT_KEY_MARKER = '::tenant:';

export function tenantScopedKey(url: string | null, tenantId: number | null): string | null {
  if (url == null || tenantId == null) return null;

  return `${url}${TENANT_KEY_MARKER}${tenantId}`;
}

/**
 * Whether an SWR cache key belongs to a single tenant. Used by the tenant-change sweep so it clears
 * only tenant-scoped entries — wiping catalog-wide caches (the tenant list above all) would empty
 * the tenant switcher mid-switch and take the current selection down with it.
 */
export function isTenantScopedKey(key: unknown): boolean {
  return typeof key === 'string' && key.includes(TENANT_KEY_MARKER);
}

/** The selected tenant, from the same store the axios request interceptor reads. */
export function currentTenantId(): number | null {
  const raw = localStorage.getItem(TENANT_ID_STORAGE_KEY);
  if (!raw) return null;

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Tenant-scoped key for imperative cache writes (mutate after a save). Only safe outside render —
 * during render the provider may not have committed a tenant change to storage yet, so use
 * `useTenantScopedKey` there instead.
 */
export function currentTenantScopedKey(url: string | null): string | null {
  return tenantScopedKey(url, currentTenantId());
}
